import { checkForSpecificConditions } from '../policy-application/pre-fill-utils/carrier-utils';
import { zipCodeToState } from '../zipCodeToState';

export type ConsumerApplicationSection = 'personal' | 'medical' | 'health' | 'beneficiaries';

export const CONSUMER_APPLICATION_SECTIONS: readonly ConsumerApplicationSection[] = [
  'personal',
  'medical',
  'health',
  'beneficiaries',
];

/** Beneficiaries only apply to products that pay a death benefit (hybrid life + LTC). */
export function applicationSectionsFor(paysDeathBenefit: boolean): ConsumerApplicationSection[] {
  return CONSUMER_APPLICATION_SECTIONS.filter(
    section => section !== 'beneficiaries' || paysDeathBenefit,
  );
}

export const EMPTY_DOCTOR: ConsumerApplicationDoctor = {
  name: '',
  practice: '',
  phone: '',
  lastVisit: '',
};

export const EMPTY_MEDICATION: ConsumerApplicationMedication = { name: '', reason: '', dose: '' };

export const EMPTY_BENEFICIARY: ConsumerApplicationBeneficiary = {
  fullName: '',
  relationship: '',
  sharePercent: null,
  dateOfBirth: '',
};

/** A YYYY-MM-DD string for a date input; JSON parsing turns these strings into Dates, so both are accepted. */
export function toDateInput(value: Date | string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

export function normalizeApplicationDates(answers: ConsumerApplicationAnswers): ConsumerApplicationAnswers {
  return {
    ...answers,
    personal: { ...answers.personal, dateOfBirth: toDateInput(answers.personal.dateOfBirth) },
    beneficiaries: answers.beneficiaries.map(beneficiary => ({
      ...beneficiary,
      dateOfBirth: toDateInput(beneficiary.dateOfBirth),
    })),
  };
}

function toYesNo(value: string | null | undefined): ConsumerYesNo {
  if (value === 'Yes') return 'yes';
  if (value === 'No') return 'no';
  return '';
}

// The intake stores the chosen labels. Only "Neither of these" means no; "I don't know / prefer
// not to answer" stays blank for the consumer to answer.
function hospitalStayAnswer(answers: string[] | null | undefined): ConsumerYesNo {
  if (!answers?.length) return '';
  if (checkForSpecificConditions(answers, ['Hospitalized', 'Visited the ER']) === 'yes') return 'yes';
  return answers.some(answer => /^neither/i.test(answer.trim())) ? 'no' : '';
}

function splitList(value: string | null | undefined): string[] {
  return (value ?? '')
    .split(/[\n,;]+/)
    .map(item => item.trim())
    .filter(Boolean);
}

/** Starts an application from what the consumer already told us in their intake. */
export function prefillApplication(intake: Partial<IntakeSurvey>): ConsumerApplicationAnswers {
  const heightInches = intake.clientHeightInches ?? null;
  const conditions = [
    ...(intake.clientDiagnosesList ?? []),
    ...(intake.clientCognitiveMemoryDiagnosis ?? []),
  ].filter(condition => !/^none/i.test(condition));

  return {
    personal: {
      firstName: intake.clientFirstName ?? '',
      lastName: intake.clientLastName ?? '',
      dateOfBirth: toDateInput(intake.clientBirthDate),
      sex: intake.clientGenderAtBirth ?? '',
      street: '',
      city: '',
      state: intake.clientZipCode ? zipCodeToState(intake.clientZipCode).stateAbbreviation : '',
      zip: intake.clientZipCode ?? '',
      phone: '',
      email: intake.clientEmail ?? '',
      maritalStatus: intake.clientMaritalStatus ?? '',
      heightFeet: heightInches ? Math.floor(heightInches / 12) : null,
      heightInches: heightInches ? heightInches % 12 : null,
      weightPounds: intake.clientWeightPounds ?? null,
    },
    health: {
      conditions: conditions.join(', '),
      tobacco: toYesNo(intake.clientSmokedRecently),
      dailyActivityHelp: toYesNo(intake.clientDifficultyWithTasks),
      memoryDiagnosis: '',
      recentHospitalStay: hospitalStayAnswer(intake.clientHospitalizedERLastTwelveMonths),
      pendingTests: '',
    },
    doctors: [{ ...EMPTY_DOCTOR }],
    medications: splitList(intake.clientMedicationIntake).map(name => ({
      ...EMPTY_MEDICATION,
      name,
    })),
    beneficiaries: [{ ...EMPTY_BENEFICIARY }],
  };
}

const isBlank = (value: string | number | null | undefined) =>
  value === null || value === undefined || String(value).trim() === '';

const REQUIRED_PERSONAL_FIELDS: (keyof ConsumerApplicationPersonal)[] = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'sex',
  'street',
  'city',
  'state',
  'zip',
  'phone',
  'email',
];

const REQUIRED_HEALTH_FIELDS: (keyof ConsumerApplicationHealth)[] = [
  'tobacco',
  'dailyActivityHelp',
  'memoryDiagnosis',
  'recentHospitalStay',
  'pendingTests',
];

// At least one doctor, then a phone number for each one listed.
function missingDoctorDetails(doctors: ConsumerApplicationDoctor[]): number {
  if (!doctors.some(doctor => !isBlank(doctor.name))) return 1;
  return doctors.filter(doctor => !isBlank(doctor.name) && isBlank(doctor.phone)).length;
}

function missingMedicationDetails(medications: ConsumerApplicationMedication[]): number {
  return medications.filter(medication => !isBlank(medication.name) && isBlank(medication.reason)).length;
}

/** How many details in a section are still empty. Sending with gaps is allowed. */
export function missingDetailsCount(
  answers: ConsumerApplicationAnswers,
  section: ConsumerApplicationSection,
): number {
  switch (section) {
    case 'personal':
      return REQUIRED_PERSONAL_FIELDS.filter(field => isBlank(answers.personal[field])).length;
    case 'health':
      return REQUIRED_HEALTH_FIELDS.filter(field => isBlank(answers.health[field])).length;
    case 'medical':
      return missingDoctorDetails(answers.doctors) + missingMedicationDetails(answers.medications);
    case 'beneficiaries':
      return answers.beneficiaries.filter(
        beneficiary => isBlank(beneficiary.fullName) || isBlank(beneficiary.relationship),
      ).length;
  }
}

export function totalMissingDetails(
  answers: ConsumerApplicationAnswers,
  sections: ConsumerApplicationSection[],
): number {
  return sections.reduce((total, section) => total + missingDetailsCount(answers, section), 0);
}
